import React, {
  FC,
  HTMLAttributes,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Box } from "@chakra-ui/core";
import { Counter, Button } from "@engyalo/storefront-components";
import {
  IConfig,
  ICartDictionary,
  IProduct,
  IProductLevel2,
  IProductLevel3,
} from "@engyalo/storefront-types/lib/plugins/model";

interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: IProduct;
  cart: ICartDictionary;
  config: IConfig;
  enablePrice?: boolean | null;
  tFunction: (translation: string) => string;
  addToCart: (
    product: IProduct,
    adder: number,
    backToCatalogue: boolean
  ) => void;
  canAddToCart: boolean;
}

export const CustomProductFooter: FC<IProps> = ({
  product,
  addToCart,
  tFunction,
  enablePrice,
  config,
  canAddToCart,
}: IProps) => {
  const [totalPrice, setTotalPrice] = useState(`${config.currencyPrefix} 0.00`);
  const [productCount, setProductCount] = useState(1);

  const addProductToCart = (): void => {
    addToCart(product, productCount, true);
  };

  const getTotalPrice = useCallback(
    (product: IProduct): number => {
      let totalPrice = 0;

      if (product && product.price) {
        const { price, customizations } = product;

        totalPrice += price;

        if (customizations) {
          customizations.forEach((level2: IProductLevel2 | null) => {
            if (level2) {
              const { customizations: level2Customizations } = level2;

              if (level2Customizations) {
                level2Customizations.forEach(
                  (level3: IProductLevel3 | null) => {
                    if (level3) {
                      const { quantity, price } = level3;

                      if (price && quantity) {
                        const level3Price = price * quantity;
                        totalPrice += level3Price;
                      }
                    }
                  }
                );
              }
            }
          });
        }
      }

      return totalPrice * productCount;
    },
    [totalPrice, productCount, product]
  );

  useEffect(() => {
    const totalPrice = getTotalPrice(product);
    setTotalPrice(`${config.currencyPrefix} ${totalPrice.toFixed(2)}`);
  }, [product, setTotalPrice, config, productCount]);

  return (
    <Box
      zIndex={1}
      paddingY="12px"
      paddingX="8px"
      boxShadow="md"
      backgroundColor="white"
      display="flex"
      justifyContent="space-between"
    >
      <Box w="50%" m="auto" flex="1">
        <Counter
          height="3.2rem"
          name={product.id || ""}
          addAction={(e): void => {
            e.stopPropagation();
            setProductCount(productCount + 1);
          }}
          minusAction={(e): void => {
            e.stopPropagation();
            if (productCount > 1) {
              setProductCount(productCount - 1);
            }
          }}
          value={productCount}
        />
      </Box>
      <Box flex="2" marginLeft="1.2rem">
        <Button isDisabled={!canAddToCart} onClick={addProductToCart}>
          {!enablePrice || enablePrice === true ? (
            <Box display="flex" justifyContent="space-between" width="100%">
              <span>{tFunction("addToCart-add")}</span>
              <span>{totalPrice}</span>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" width="100%">
              <span>{tFunction("addToCart-add")}</span>
            </Box>
          )}
        </Button>
      </Box>
    </Box>
  );
};
